import { Dispatch, SetStateAction } from 'react';
import { ToggleBtn } from '@components/common/ToggleSwitch';
import dayjs from '@lib/dayjs';
import Format from '@util/formatter';
import { getBlockTooltipMessage } from '@util/ticketMessages';

export enum TicketTypeEnum {
  TICKET_ALL = 0,
  TICKET_SEAT_ONE_TIME = 1,
  TICKET_SEAT_PERIODIC = 2,
  TICKET_SEAT_TIME_BASED = 3,
  TICKET_SEMINAR = 4,
  TICKET_LOCKER = 5,
}

export const TicketTypeLabel: Record<number, string> = {
  [TicketTypeEnum.TICKET_SEAT_ONE_TIME]: '일회권',
  [TicketTypeEnum.TICKET_SEAT_PERIODIC]: '기간권',
  [TicketTypeEnum.TICKET_SEAT_TIME_BASED]: '시간정액권',
  [TicketTypeEnum.TICKET_SEMINAR]: '세미나실',
  [TicketTypeEnum.TICKET_LOCKER]: '사물함',
};

export const getTicketTypeText = (type: TicketType) => {
  try {
    return TicketTypeLabel[type] || '-';
  } catch (error) {
    return '-';
  }
};

export const getTicketRemainTimeInSeconds = (ticket: Ticket) => {
  const nowTime = dayjs();
  const startTime = dayjs(ticket.startDate);
  const endTime = dayjs(ticket.endDate);
  const today_diff = dayjs.duration(endTime.diff(nowTime)).asMilliseconds();
  const lastInTime = dayjs(ticket.lastIn);
  const lastOutTime = dayjs(ticket.lastOut);
  const lastTime = dayjs.duration(lastInTime.diff(lastOutTime)).asMilliseconds();
  const remainTimeData = dayjs.duration(nowTime.diff(lastInTime)).asSeconds();
  const seminarNotUsing = dayjs.duration(endTime.diff(nowTime)).asSeconds();
  const excessData = dayjs.duration(nowTime.diff(lastOutTime)).asSeconds();
  //현재시간기준으로 시작시간하고의 차이
  const today_diff2 = dayjs.duration(nowTime.diff(startTime)).asMilliseconds();
  //일회권, 기간권, 시간정액권 사용중이면서, 퇴장기록이 없는경우
  return ((ticket.isUsing === 1 && !ticket.lastOut && ticket.lastIn) ||
    (ticket.isUsing === 1 && ticket.lastOut && ticket.lastIn && lastTime > 0)) &&
    (ticket.type === 1 || ticket.type === 2 || ticket.type === 3)
    ? Format.formatSecondsToHHmmss(ticket.remainTime - Math.ceil(remainTimeData))
    : //세미나실인경우 (입실처리x 시간초과x)
    ticket.type === 4 && today_diff > 0 && today_diff2 > 0
    ? Math.ceil(seminarNotUsing)
    : //세미나실인경우 (입실처리x 시간초과o)
    ticket.type === 4 && today_diff < 0 && today_diff2 > 0
    ? '00:00:00'
    : // 일회권,기간권, 시간정액권 사용중이면서 시간을 초과한경우
    ticket.isUsing === 2 && ticket.lastOut
    ? +Math.ceil(excessData)
    : Format.formatSecondsToHHmmss(ticket.remainTime); // 위 조건에 해당하지 않을 때는 백엔드 remanTime 그대로 출력!
};

export const processTicketList = ({
  list,
  tooltip,
  setTooltip,
  handleOpenDetail,
  handleBlockTicket,
  handleExitTicket,
  handleAddTimeToTicket,
}: {
  list: TicketEdit[];
  tooltip?: any;
  setTooltip?: Dispatch<SetStateAction<any>>;
  handleOpenDetail?: (ticket: TicketEdit) => void;
  handleBlockTicket?: (ticket: TicketEdit) => void;
  handleExitTicket?: (ticket: TicketEdit) => void;
  handleAddTimeToTicket?: (ticket: TicketEdit) => void;
}) => {
  const result = list.map((ticket, idx) => {
    const t1 = dayjs(ticket.endDate, 'YYYY-MM-DD HH:mm:ss'); // 이용권 종료 날짜
    const t3 = dayjs(new Date()); // 오늘 날짜
    const nowTime = dayjs();
    const endTime = dayjs(ticket.endDate);
    const today_diff = dayjs.duration(endTime.diff(nowTime)).asMilliseconds();

    return {
      ...ticket,
      // 입장권 이름 출력
      typeName:
        ticket.type === 1 && ticket.seatNumber
          ? ` 일회권 (${ticket.seatNumber}번)`
          : ticket.type === 1 && !ticket.seatNumber
          ? ' 일회권'
          : ticket.type === 4 && ticket.seatNumber
          ? ` 세미나실 (${ticket.seatNumber}번)`
          : ticket.type === 4 && !ticket.seatNumber
          ? ' 세미나실'
          : ticket.type === 5 && ticket.seatNumber
          ? ` 사물함 (${ticket.seatNumber}번)`
          : ticket.type === 5 && !ticket.seatNumber
          ? ' 사물함'
          : ticket.type === 2
          ? ` 기간권 - ${ticket.seatType === 0 ? '자유석' : '고정석'}`
          : ticket.type === 3
          ? '시간정액권'
          : '사물함',

      // 입장권 이름 출력
      type1:
        ticket.type === 1 ? (
          <p style={{ color: '#EBAF22', margin: 0 }}>
            일회권
            {ticket.seatNumber ? `(${ticket.seatNumber}번)` : ''}
          </p>
        ) : ticket.type === 2 ? (
          <>
            {ticket.seatType === 0 ? (
              <p style={{ color: '#E03D64', margin: 0 }}>기간권 - 자유석</p>
            ) : (
              <p style={{ color: '#1BBABC', margin: 0 }}>기간권 - 고정석</p>
            )}
          </>
        ) : ticket.type === 3 ? (
          <p style={{ color: '#33946B', margin: 0 }}>시간정액권</p>
        ) : ticket.type === 4 ? (
          <p style={{ color: '#3E67B6', margin: 0 }}>
            세미나실
            {ticket.seatNumber ? `(${ticket.seatNumber}번)` : ''}
          </p>
        ) : (
          <p style={{ color: '#AB84FF', margin: 0 }}>
            사물함
            {ticket.seatNumber ? `(${ticket.seatNumber}번)` : ''}
          </p>
        ),

      // 전화번호
      phoneNumtag: ticket.phoneNum,
      // 마지막 입장 시간
      lastIn: ticket.lastIn ? dayjs(ticket.lastIn).format('YYYY-MM-DD HH:mm:ss') : '',
      // 이용권 시작 날짜
      startDate: ticket.startDate ? dayjs(ticket.startDate).format('YYYY-MM-DD HH:mm:ss') : '',
      action:
        +ticket.type === 2 && +ticket.seatType === 0 // 기간권이고 자유석일때
          ? Format.formatSecondsToHHmmss(ticket.remainTime)
          : Format.formatSecondsToHHmmss(ticket.remainTime + (ticket.usedTime || 0)),
      // 유효기간 -> 시간정액권, 사물함만 존재
      // 종료시간이 현재시간보다 클 경우 표시
      expireDate2:
        (ticket.type === 3 || ticket.type === 5) &&
        Math.ceil(dayjs.duration(dayjs(t1).diff(t3)).asDays()) > 0
          ? `${Math.ceil(dayjs.duration(dayjs(t1).diff(t3)).asDays())}일`
          : '-',
      T_isUsing:
        ticket.isUsing === 1 ? (
          <div>사용중</div>
        ) : ticket.isUsing === 2 ? (
          <div style={{ color: '#F04E36' }}>시간 초과</div>
        ) : ticket.lastOut ? (
          <div style={{ color: '#53852A' }}>퇴장완료</div>
        ) : (
          <div>사용전</div>
        ),

      usingTimeKeyword:
        ticket.isUsing === 1
          ? '사용중'
          : ticket.isUsing === 2
          ? '시간초과'
          : ticket.lastOut
          ? '퇴장완료'
          : '사용전',

      blockKeyword: ticket.block === 1 ? '티켓사용불가' : '티켓사용가능',
      T_block:
        ticket.block === 1 ? (
          <p style={{ color: '#F04E36', margin: 0 }}>티켓사용불가</p>
        ) : (
          <p style={{ margin: 0 }}>티켓사용가능</p>
        ),
      action2: (
        <button
          className="btn-primary btn-sm"
          id={idx.toString()}
          onClick={() => {
            handleOpenDetail && handleOpenDetail(ticket);
          }}>
          자세히
        </button>
      ),

      outButton:
        ticket.isUsing === 1 ? (
          <button
            className="btn-secondary btn-sm"
            onClick={() => {
              handleExitTicket && handleExitTicket(ticket);
            }}>
            강제퇴장처리
          </button>
        ) : ticket.isUsing === 2 ? (
          <button
            className="btn-success btn-sm"
            onClick={() => {
              handleAddTimeToTicket && handleAddTimeToTicket(ticket);
            }}>
            초과결제해제
          </button>
        ) : (
          <button
            disabled
            className="btn-sm"
            onClick={() => {
              handleExitTicket && handleExitTicket(ticket);
            }}>
            퇴장처리
          </button>
        ),

      last2: ticket.lastOut ? dayjs(ticket.lastOut).format('YYYY-MM-DD HH:mm:ss') : '',
      isUsingKeyword:
        ticket.isUsing === 1 ? '강제퇴장처리' : ticket.isUsing === 2 ? '초과결제해제' : '퇴장처리',

      remainTimeSec: getTicketRemainTimeInSeconds(ticket),

      //엑셀
      remainTime2: Format.calculateTicketRemainTime(ticket),

      // 사용가능
      ticketBlock: (() => {
        const isExpired = today_diff <= 0;
        //입장권 차단 활성화
        //1. 끝나는 시간이 지나지 않았고, 차단이 가능한 상태인경우(block:0), 남아있는 시간이 있는 경우
        if (
          (today_diff > 0 && +ticket.block === 0 && ticket.remainTime > 0) ||
          (ticket.type === 5 && today_diff > 0 && ticket.block !== 1)
        ) {
          return (
            <ToggleBtn>
              <input
                type="checkbox"
                checked={ticket.block === 0}
                onClick={() => handleBlockTicket && handleBlockTicket(ticket)}
              />
              <span className="onoff-switch"></span>
            </ToggleBtn>
          );
        }
        //입장권 허용 활성화
        //1. 끝나는 시간이 지나지 않았고,차단되어있으면서, 남아있는 시간이 있는 경우
        if (
          today_diff > 0 &&
          +ticket.block === 1 &&
          ticket.remainTime > 0 &&
          (ticket.type === 2 || ticket.type === 3)
        ) {
          return (
            <ToggleBtn>
              <input
                type="checkbox"
                checked={ticket.block === 0}
                onClick={() => handleBlockTicket && handleBlockTicket(ticket)}
              />
              <span className="onoff-switch"></span>
            </ToggleBtn>
          );
        }
        // 조작할 수 없는 상태 disabled
        return (
          <ToggleBtn
            onMouseEnter={() => {
              setTooltip &&
                setTooltip({
                  enabled: true,
                  message: getBlockTooltipMessage(ticket, isExpired),
                });
            }}
            onMouseLeave={() => setTooltip && setTooltip({ ...tooltip, enabled: false })}>
            <input
              type="checkbox"
              disabled
              onClick={() => handleBlockTicket && handleBlockTicket(ticket)}
            />
            <span className="onoff-switch disabled"></span>
          </ToggleBtn>
        );
      })(),

      ticketBlockKeyword:
        //입장권 차단 활성화 키워드
        //1. 끝나는 시간이 지나지 않았고, 차단이 가능한 상태인경우(block:0), 남아있는 시간이 있는 경우
        today_diff > 0 && +ticket.block === 0 && ticket.remainTime > 0
          ? '입장권 차단'
          : //입장권 허용 활성화
          //1. 끝나는 시간이 지나지 않았고,차단되어있으면서, 남아있는 시간이 있는 경우
          today_diff > 0 && +ticket.block === 1 && ticket.remainTime > 0
          ? '입장권 허용'
          : '입장권 차단',
    };
  });
  return result as TicketEdit[];
};
